/**
 * Los pasos del asistente (§9).
 *
 * Se aísla de los componentes porque es la lógica que decide qué ve el cliente,
 * y conviene poder comprobarla sin montar la interfaz.
 */

export type Paso = 'entidad' | 'acceso' | 'servicio' | 'hora' | 'confirmacion';

const TODOS: Paso[] = ['entidad', 'acceso', 'servicio', 'hora', 'confirmacion'];

/**
 * Los pasos que ese cliente va a ver.
 *
 * El de Servicio desaparece cuando el cliente tiene **un solo servicio
 * contratado** —lo decide el servidor (D-03)—, de modo que llega a la rejilla de
 * horas en tres pantallas. La barra de progreso muestra solo estos.
 */
export function pasosVisibles(saltarPasoDeServicio: boolean): Paso[] {
  return saltarPasoDeServicio ? TODOS.filter((paso) => paso !== 'servicio') : TODOS;
}

/** En qué posición va el cliente, para la barra de progreso. */
export function posicionDelPaso(paso: Paso, visibles: Paso[]): number {
  const indice = visibles.indexOf(paso);
  return indice === -1 ? 0 : indice + 1;
}

/** El paso siguiente, o el mismo si ya es el último. */
export function siguientePaso(paso: Paso, visibles: Paso[]): Paso {
  const indice = visibles.indexOf(paso);
  if (indice === -1 || indice === visibles.length - 1) return paso;
  return visibles[indice + 1] ?? paso;
}

/**
 * En qué estado queda el asistente al empezar otra reserva.
 *
 * Cuando el cliente tiene **un solo servicio contratado**, el paso de Servicio
 * no existe (D-03) y nadie va a volver a elegirlo, así que hay que reponerlo:
 * mandar al cliente al paso de la hora con el servicio a `null` deja la pantalla
 * en blanco, porque ese paso no puede pintarse sin servicio.
 */
export function estadoAlReiniciar<T>(sesion: { saltarPasoDeServicio: boolean; servicios: T[] }): {
  paso: Paso;
  servicio: T | null;
} {
  if (!sesion.saltarPasoDeServicio) return { paso: 'servicio', servicio: null };
  return { paso: 'hora', servicio: sesion.servicios[0] ?? null };
}

/** El paso anterior, o el mismo si ya es el primero. */
export function pasoAnterior(paso: Paso, visibles: Paso[]): Paso {
  const indice = visibles.indexOf(paso);
  if (indice <= 0) return paso;
  return visibles[indice - 1] ?? paso;
}
